import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { routes, token_keys } from '../../constants';
import api from '../../api';
import useStore from '../../store';

export default function SsoCallback({ authentication }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setActiveUser = useStore((state) => state.setActiveUser);
  const setAuthenticated = useStore((state) => state.setAuthenticated);
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // The backend redirects auth failures to /login?e=..., but if it ever
    // lands an error on this callback (WorkOS or Entra), surface it and bounce
    // back to login rather than getting stuck on "Signing you in…".
    const errorParam = searchParams.get('error') || searchParams.get('e');
    if (errorParam) {
      setError(errorParam);
      setTimeout(
        () => navigate(`${routes.login}?e=${encodeURIComponent(errorParam)}`, { replace: true }),
        1500
      );
      return;
    }

    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');
    const expiresIn = searchParams.get('expires_in');

    if (!accessToken || !refreshToken) {
      setError('Missing tokens in SSO callback');
      setTimeout(() => navigate(routes.login, { replace: true }), 1500);
      return;
    }

    const finishLogin = async () => {
      try {
        const issuedAt = Date.now();
        const ttl = parseInt(expiresIn, 10) || 900;
        localStorage.setItem(token_keys.expiration, ttl.toString());
        localStorage.setItem(token_keys.issued_at, issuedAt.toString());
        localStorage.setItem(token_keys.access_token, accessToken);
        localStorage.setItem(token_keys.refresh_token, refreshToken);
        // Completing SSO is activity: start the idle clock now so the new
        // session can't inherit a stale stamp from a previous one.
        localStorage.setItem(token_keys.last_activity, issuedAt.toString());

        const userResponse = await api.getCurrentUser();
        setActiveUser(userResponse.data);
        setAuthenticated(true);
        if (authentication) authentication(true);
        navigate(routes.home, { replace: true });
      } catch (e) {
        localStorage.removeItem(token_keys.access_token);
        localStorage.removeItem(token_keys.refresh_token);
        localStorage.removeItem(token_keys.expiration);
        localStorage.removeItem(token_keys.issued_at);
        localStorage.removeItem(token_keys.last_activity);
        setError('Failed to complete SSO sign-in');
        setTimeout(() => navigate(routes.login, { replace: true }), 1500);
      }
    };

    finishLogin();
  }, [searchParams, navigate, setActiveUser, setAuthenticated, authentication]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <CircularProgress />
          <Typography>Signing you in…</Typography>
        </>
      )}
    </Box>
  );
}
